import { useState, useCallback, useEffect } from 'react';
import { useGuestsQuery, useGuestMutations } from '../../hooks/useGuests';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth-context';
import { Mail, Pencil, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { AddGuestModal } from './AddGuestModal';
import { SendQrModal } from '../delivery/SendQrModal';

export function GuestTable({
  filterPayDate,
  filterRegistered,
  filterDinner,
  onFilteredTotalChange,
}: {
  filterPayDate?: string;
  filterRegistered?: string;
  filterDinner?: string;
  onFilteredTotalChange?: (total: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('l_name');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data, isLoading, isError } = useGuestsQuery({
    search,
    field: searchField,
    page,
    limit,
    pay_date: filterPayDate,
    registered: filterRegistered,
    dinner: filterDinner,
  });
  const { bulkUpdate, deleteGuest, createGuest, updateGuest } = useGuestMutations();
  const { isAdmin, isAccountant } = useAuth();
  const canEdit = isAdmin || isAccountant;
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; l_name: string; f_name: string; company: string } | null>(null);
  const [sendTarget, setSendTarget] = useState<{ id: number; l_name: string; f_name: string; email: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{ id: number; l_name: string; f_name: string; p_name: string; company: string; role: string; country: string; email: string } | null>(null);

  const guests = data?.guests ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  useEffect(() => {
    onFilteredTotalChange?.(total);
  }, [total, onFilteredTotalChange]);

  const toggleField = (id: number, field: string, value: boolean) => {
    bulkUpdate.mutate([{ id, field, value }]);
  };

  const handleExport = useCallback(() => {
    api.guests.exportExcel({
      search: search || undefined,
      field: searchField,
      pay_date: filterPayDate,
      registered: filterRegistered,
      dinner: filterDinner,
    });
  }, [search, searchField, filterPayDate, filterRegistered, filterDinner]);

  const selectStyle = 'rounded-lg border border-ct-border bg-ct-surface px-4 py-2.5 text-sm text-ct-text focus:ring-2 focus:ring-ct-primary focus:ring-offset-1 focus:border-ct-primary focus:outline-none transition-all duration-200';

  const inputStyle = 'flex-1 min-w-[200px] rounded-lg border border-ct-border bg-ct-surface px-4 py-2.5 text-sm text-ct-text placeholder:text-ct-text-muted/50 focus:ring-2 focus:ring-ct-primary focus:ring-offset-1 focus:border-ct-primary focus:outline-none transition-all duration-200';

  return (
    <div>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <select
          value={searchField}
          onChange={(e) => setSearchField(e.target.value)}
          className={selectStyle}
        >
          <option value="l_name">Фамилия</option>
          <option value="f_name">Имя</option>
          <option value="company">Компания</option>
          <option value="country">Страна</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Поиск..."
          className={inputStyle}
        />
        <button
          onClick={() => setAddOpen(true)}
          className="whitespace-nowrap rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-5 py-2.5 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
        >
          + Добавить участника
        </button>
        <button
          onClick={handleExport}
          className="whitespace-nowrap rounded-lg border border-ct-border bg-ct-surface px-5 py-2.5 text-sm font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md hover:border-ct-primary/30 active:scale-[0.98]"
        >
          Выгрузить список в Excel
        </button>
      </div>

      {isError && guests.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          Не удалось обновить данные. Показаны последние загруженные данные.
        </div>
      )}

      <div className="overflow-x-auto rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary">
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">#</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Фамилия</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Имя</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Компания</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Страна</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Оплатил</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Ужин</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90">Зарегистрирован</th>
              <th className="px-4 py-3 font-semibold text-ct-on-primary/90 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-ct-text-muted">
                  Загрузка...
                </td>
              </tr>
            ) : isError && guests.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-red-500">
                  Ошибка загрузки данных. Проверьте соединение.
                </td>
              </tr>
            ) : guests.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-ct-text-muted">
                  Участники не найдены
                </td>
              </tr>
            ) : (
              guests.map((guest, i) => (
                <tr key={guest.id} className="border-b border-ct-border last:border-0 hover:bg-indigo-50/40 transition-colors duration-150">
                  <td className="px-4 py-3 text-ct-text-muted">{(page - 1) * limit + i + 1}</td>
                  <td className="px-4 py-3 font-semibold text-ct-text">{guest.l_name}</td>
                  <td className="px-4 py-3 text-ct-text">{guest.f_name}</td>
                  <td className="px-4 py-3 text-ct-text-muted">{guest.company}</td>
                  <td className="px-4 py-3 text-ct-text-muted">{guest.country}</td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={guest.pay_date}
                      onChange={(e) => toggleField(guest.id, 'pay_date', e.target.checked)}
                      className="h-4 w-4 accent-ct-primary"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={guest.dinner}
                      disabled={!guest.pay_date}
                      onChange={(e) => toggleField(guest.id, 'dinner', e.target.checked)}
                      className={`h-4 w-4 accent-ct-primary ${!guest.pay_date ? 'opacity-40 cursor-not-allowed' : ''}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={guest.registered}
                      disabled={!guest.pay_date}
                      onChange={(e) => toggleField(guest.id, 'registered', e.target.checked)}
                      className={`h-4 w-4 accent-ct-primary ${!guest.pay_date ? 'opacity-40 cursor-not-allowed' : ''}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {canEdit && (
                        <button
                          onClick={() => setEditTarget({ id: guest.id, l_name: guest.l_name, f_name: guest.f_name, p_name: guest.p_name, company: guest.company, role: guest.role, country: guest.country, email: guest.email })}
                          className="text-ct-text-muted hover:text-ct-primary transition-colors"
                          title="Редактировать участника"
                          aria-label="Редактировать участника"
                        >
                          <Pencil className="h-6 w-6" strokeWidth={2} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => setSendTarget({ id: guest.id, l_name: guest.l_name, f_name: guest.f_name, email: guest.email })}
                          className="text-ct-primary hover:text-ct-primary/70 transition-colors"
                          title="Отправить QR-код по email"
                          aria-label="Отправить QR-код по email"
                        >
                          <Mail className="h-6 w-6" strokeWidth={2} />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteTarget({ id: guest.id, l_name: guest.l_name, f_name: guest.f_name, company: guest.company })}
                        className="text-red-400 hover:text-red-600 transition-colors"
                        title="Удалить участника"
                        aria-label="Удалить участника"
                      >
                        <Trash2 className="h-6 w-6" strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-center gap-3">
        {totalPages > 1 && (
          <>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-ct-border bg-ct-surface px-4 py-2 text-sm font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary active:scale-[0.98] disabled:opacity-50"
            >
              Назад
            </button>
            <span className="text-sm text-ct-text-muted font-semibold">
              Стр. {page} из {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-ct-border bg-ct-surface px-4 py-2 text-sm font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary active:scale-[0.98] disabled:opacity-50"
            >
              Вперёд
            </button>
          </>
        )}
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
          className={selectStyle}
        >
          <option value={10}>10 / стр.</option>
          <option value={20}>20 / стр.</option>
          <option value={50}>50 / стр.</option>
        </select>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Удаление участника"
        message={`Вы уверены, что хотите удалить участника ${deleteTarget?.l_name} ${deleteTarget?.f_name} из ${deleteTarget?.company}? Это действие нельзя отменить.`}
        confirmLabel="Удалить"
        cancelLabel="Отмена"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget !== null) {
            deleteGuest.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <AddGuestModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={(data) => {
          createGuest.mutate(data, {
            onSuccess: () => setAddOpen(false),
          });
        }}
        saving={createGuest.isPending}
      />

      <AddGuestModal
        open={editTarget !== null}
        mode="edit"
        initialData={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={(data) => {
          if (editTarget !== null) {
            updateGuest.mutate(
              { id: editTarget.id, data },
              {
                onSuccess: () => setEditTarget(null),
              },
            );
          }
        }}
        saving={updateGuest.isPending}
      />

      <SendQrModal
        open={sendTarget !== null}
        guestId={sendTarget?.id ?? null}
        participantName={sendTarget ? `${sendTarget.l_name} ${sendTarget.f_name}` : ''}
        storedEmail={sendTarget?.email ?? ''}
        onClose={() => setSendTarget(null)}
      />
    </div>
  );
}
